import { z } from 'zod/v4';

import { foldSynonyms, isString } from './fold-helpers';
import { normalizeWidgetArgs } from './normalize-widget-args';

export const WIDGET_TYPE_SYNONYM_KEYS = ['type', 'widget_type'] as const;

/**
 * Type-agnostic create args — the `create_widget` twin of `update_widget.schema.ts`'s
 * `updateWidgetArgsSchema`. `widgetType` is a free string here rather than a shared enum:
 * ai-connector cannot depend on `apps/mcp-backend`'s `widget-types.registry.ts` (wrong direction —
 * that registry already depends on this package), and duplicating its name list here would be a
 * third place for it to drift out of sync. The server is the enforcement point: an unknown or
 * uncreatable type comes back as a 400 that names what it does support, which is enough for the
 * model to correct itself on the next call — the same trade `update_widget` already makes for its
 * fields.
 *
 * `data` stays an open record for the same reason as `update_widget`'s: which keys are valid
 * depends on `widgetType`, and this tool exists precisely for when the caller does not want (or
 * need) the type-specific tool's own schema — pair it with `get_widget_schema` when the shape is
 * unknown.
 */
const createWidgetObjectSchema = z.object({
    widgetType: z
        .string()
        .describe(
            'Backend widget type to create — e.g. "button", "text", "image", "social-networks", "video", ' +
                '"form", "payment", "navigation", "logo", "menu", "accordion", "indent". Call get_widget_schema ' +
                'first if unsure of the exact name or of what `data` this type accepts, or PREFER the ' +
                'type-specific tool (create_button_widget, create_text_widget, …) when you already know the ' +
                'type: those describe their own fields, and this one cannot.'
        ),
    columnId: z.string().describe('Id of the column to create the widget in.'),
    index: z
        .number()
        .int()
        .min(0)
        .optional()
        .describe("Position within the column's widget list (0-based). Omit to append at the end."),
    data: z
        .record(z.string(), z.unknown())
        .optional()
        .describe(
            'Initial settings for the widget. Which fields are valid depends on widgetType — the server ' +
                'validates against the real schema for that type and, if you get it wrong, the error names the ' +
                'type and the valid fields, so read it and retry.'
        ),
});

export const createWidgetArgsSchema = z.preprocess(
    (raw) =>
        normalizeWidgetArgs(
            raw,
            (args, data) => {
                foldSynonyms(args, args, 'widgetType', WIDGET_TYPE_SYNONYM_KEYS, isString);
                return data;
            },
            WIDGET_TYPE_SYNONYM_KEYS
        ),
    createWidgetObjectSchema
);
